/**
 * Install CTA — daemon-aware action panel for the public marketplace.
 *
 * Three states:
 *   1. probing — initial 1.5s daemon detection in flight, show neutral spinner
 *   2. reachable — local daemon is running, "Open Axon dashboard" button
 *   3. unreachable — show CLI install instructions
 *
 * For v1 the "Open Axon dashboard" button just opens the dashboard
 * root in a new tab. The user navigates manually to the Marketplace
 * tab + clicks Install. A future patch will deeplink to a query
 * param `?install=<id>` that auto-opens the install dialog (deferred
 * to keep PR #1 focused on the static-browse surface).
 */
import { useState } from "react";
import { Icon } from "./Icon.js";
import { LOCAL_DASHBOARD_URL, type DaemonStatus } from "../lib/daemon.js";

export interface InstallCTAProps {
  daemon: DaemonStatus | null; // null = still probing
  /** Strategy id — surfaced to the user so they know which one to
   *  install once they land in the local dashboard. */
  strategyId: string;
}

export function InstallCTA({ daemon, strategyId }: InstallCTAProps) {
  if (daemon === null) {
    return (
      <div className="cta cta-probing">
        <span className="cta-spin" />
        <span>Detecting local Axon daemon…</span>
      </div>
    );
  }

  if (daemon.reachable) {
    // Deeplink the dashboard with `?install=<id>` so its App.tsx
    // auto-switches to the Marketplace tab + pops the detail dialog
    // open for this strategy. Saves the user a click; preserves the
    // bearer token (already in the URL fragment of the dashboard) by
    // letting the daemon's existing handler do the routing.
    const deeplink = `${LOCAL_DASHBOARD_URL}/?install=${encodeURIComponent(strategyId)}`;
    return (
      <div className="cta cta-ok">
        <button
          className="cta-btn primary"
          onClick={() => window.open(deeplink, "_blank", "noopener")}
        >
          <Icon name="external" size={12} />
          Install in Axon
        </button>
        <p className="cta-hint">
          Local daemon detected. Click to open the dashboard with the{" "}
          <strong>{strategyId}</strong> install dialog already open.
        </p>
        <Styles />
      </div>
    );
  }

  return (
    <div className="cta cta-fallback">
      <div className="cta-fallback-head">
        <Icon name="terminal" size={12} />
        <span>No local Axon daemon detected — install in 30 seconds:</span>
      </div>
      <ol className="cta-steps">
        <li>
          <span className="cta-step-lab">1. Install the CLI</span>
          <CodeCopy command="npm install -g @axon-trading/cli" />
        </li>
        <li>
          <span className="cta-step-lab">2. Run it</span>
          <CodeCopy command="axon" />
        </li>
        <li>
          <span className="cta-step-lab">3. Open the dashboard, browse the Marketplace tab,
            click Install on <code className="cta-id">{strategyId}</code>.</span>
        </li>
      </ol>
      <p className="cta-foot">
        Axon is non-custodial — your keys stay on your machine. Learn more:{" "}
        <a
          href="https://axonterminal.ai"
          target="_blank"
          rel="noopener noreferrer"
        >
          axonterminal.ai
        </a>
      </p>
      <Styles />
    </div>
  );
}

function CodeCopy({ command }: { command: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="cta-cmd">
      <code>{command}</code>
      <button
        type="button"
        className="cta-copy"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(command);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          } catch {
            /* clipboard blocked — user can still select + copy manually */
          }
        }}
        aria-label="Copy command to clipboard"
      >
        {copied ? <Icon name="check" size={11} /> : <Icon name="copy" size={11} />}
      </button>
    </div>
  );
}

function Styles() {
  return (
    <style>{`
      .cta { display: flex; flex-direction: column; gap: 10px; }
      .cta-spin { width: 12px; height: 12px; border: 2px solid var(--at-hairline);
        border-top-color: var(--at-accent); border-radius: 50%;
        animation: cta-spin 0.8s linear infinite; }
      @keyframes cta-spin { to { transform: rotate(360deg); } }
      .cta-probing { flex-direction: row; align-items: center;
        font-family: var(--at-font-mono); font-size: 11px; color: var(--at-muted); }

      .cta-btn { display: inline-flex; align-items: center; justify-content: center;
        gap: 6px; padding: 10px 14px;
        border-radius: var(--at-radius-md); border: 1px solid var(--at-hairline);
        background: transparent; color: var(--at-fg); cursor: pointer;
        font-family: inherit; font-size: 13px; font-weight: 500;
        transition: all var(--at-dur-fast) var(--at-ease); }
      .cta-btn.primary { background: var(--at-accent); color: var(--at-bg);
        border-color: transparent; font-weight: 600; }
      .cta-btn.primary:hover { filter: brightness(1.1); }

      .cta-hint { margin: 0; font-size: 11.5px; color: var(--at-muted); line-height: 1.55; }
      .cta-hint strong { color: var(--at-fg); }
      .cta-id { font-family: var(--at-font-mono); font-size: 10.5px;
        background: var(--at-bg); padding: 1px 5px; border-radius: var(--at-radius-sm);
        border: 1px solid var(--at-hairline); color: var(--at-fg); }

      .cta-fallback-head { display: flex; align-items: center; gap: 8px;
        font-size: 12px; color: var(--at-fg-dim); padding-bottom: 4px; }
      .cta-steps { list-style: none; padding: 0; margin: 0;
        display: flex; flex-direction: column; gap: 8px; }
      .cta-steps li { display: flex; flex-direction: column; gap: 4px;
        padding: 8px 10px;
        background: var(--at-bg); border: 1px solid var(--at-hairline);
        border-radius: var(--at-radius-md); }
      .cta-step-lab { font-family: var(--at-font-mono); font-size: 10px;
        letter-spacing: 0.16em; text-transform: uppercase; color: var(--at-muted); }
      .cta-step-lab strong { color: var(--at-fg); text-transform: none;
        font-family: var(--at-font-body); letter-spacing: normal; }
      .cta-cmd { display: flex; align-items: center; justify-content: space-between; gap: 8px;
        padding: 6px 8px; background: var(--at-surface);
        border: 1px solid var(--at-hairline); border-radius: var(--at-radius-sm); }
      .cta-cmd code { font-family: var(--at-font-mono); font-size: 12px; color: var(--at-fg);
        white-space: nowrap; overflow-x: auto; }
      .cta-copy { background: transparent; border: 0; color: var(--at-muted);
        cursor: pointer; padding: 3px; border-radius: var(--at-radius-sm); flex-shrink: 0; }
      .cta-copy:hover { color: var(--at-fg); background: var(--at-bg); }

      .cta-foot { margin: 0; font-size: 11px; color: var(--at-muted); line-height: 1.5; }
      .cta-foot a { color: var(--at-accent); }
      .cta-foot a:hover { text-decoration: underline; }
    `}</style>
  );
}
